import type { ReactNode } from "react"
import { Modal, Box } from "@mui/material"
import type { SxProps, Theme } from "@mui/material/styles"

export interface CenteredModalProps {
    open: boolean
    onClose: () => void
    children: ReactNode
    /** Width of the modal (default: "50%") */
    width?: string | number
    /** Optional sx to merge with the outer Box */
    sx?: SxProps<Theme>
    /** When true, backdrop click and Escape do not close the modal */
    preventBackdropClose?: boolean
}

/**
 * Reusable centered modal skeleton with consistent layout:
 * - Centered in viewport, scrollable content, paper background.
 * Use for publish, confirm, and similar content modals.
 */
const CenteredModal = ({
    open,
    onClose,
    children,
    width = "50%",
    sx = [],
    preventBackdropClose = false,
}: CenteredModalProps) => {
    const handleClose = (_event: object, reason?: "backdropClick" | "escapeKeyDown") => {
        if (preventBackdropClose && (reason === "backdropClick" || reason === "escapeKeyDown")) {
            return
        }
        onClose()
    }

    return (
        <Modal open={open} onClose={handleClose} disableEscapeKeyDown={preventBackdropClose}>
            <Box
                sx={[
                    {
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width,
                        maxHeight: "90vh",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        "@media (max-height: 600px)": {
                            maxHeight: "95vh",
                            top: "50%",
                        },
                    },
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
            >
                <Box
                    sx={{
                        overflowY: "auto",
                        overflowX: "hidden",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        padding: "24px",
                        gap: 3,
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Modal>
    )
}

export default CenteredModal
